import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import AutoxiumLogo from '../components/AutoxiumLogo';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });

  const from = location.state?.from || '/';
  const redirectMessage = location.state?.message;

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.identifier || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(formData.identifier, formData.password);
      toast.success('Login successful!');
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 grid-pattern" data-testid="login-page">
      <Card className="w-full max-w-md border-2 border-structure shadow-xl">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-block mx-auto mb-6">
            <AutoxiumLogo width={80} height={64} />
          </Link>
          <h1 className="font-chivo text-2xl uppercase tracking-tight text-chassis">Login</h1>
          <p className="text-sm text-muted-foreground">Sign in to access your account</p>
        </CardHeader>
        <CardContent>
          {redirectMessage && (
            <p
              className="text-sm text-autoxium-blue bg-autoxium-blue/10 border border-autoxium-blue/20 rounded-md px-3 py-2 mb-4 text-center"
              data-testid="login-redirect-message"
            >
              {redirectMessage}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier">Email or Mobile Number</Label>
              <Input
                id="identifier"
                name="identifier"
                type="text"
                placeholder="Enter email or mobile number"
                value={formData.identifier}
                onChange={handleChange}
                className="h-12 border-2 focus:border-autoxium-blue"
                data-testid="login-identifier-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-12 border-2 pr-10 focus:border-autoxium-blue"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link 
                to="/forgot-password" 
                className="text-sm text-autoxium-blue hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Link>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 bg-autoxium-blue hover:bg-autoxium-blue/90 font-bold uppercase tracking-wider"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Login'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link
              to="/register"
              state={location.state}
              className="text-autoxium-blue hover:underline font-medium"
              data-testid="register-link"
            >
              Register
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
